import React from 'react';
import Head from 'next/head';

/**
 * Drop-in replacement for next-seo v6 `NextSeo` (next-seo v7 is JSON-LD only).
 */
export default function PageSeo({
    title,
    description,
    canonical,
    openGraph,
    twitter,
    additionalMetaTags = [],
    additionalLinkTags = [],
    noindex = false,
    nofollow = false,
}) {
    const og = openGraph || {};
    const tw = twitter || {};
    const ogTitle = og.title || title;
    const ogDescription = og.description || description;
    const ogUrl = og.url || canonical;
    const ogImages = og.images || [];
    const primaryImage = ogImages[0];

    let robotsContent = 'index, follow';
    if (noindex || nofollow) {
        robotsContent = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`;
    }
    const robotsFromMeta = additionalMetaTags.find((t) => t.name === 'robots');
    if (robotsFromMeta?.content) {
        robotsContent = robotsFromMeta.content;
    }

    const metaTags = additionalMetaTags.filter((t) => t.name !== 'robots');

    return (
        <Head>
            {title ? <title>{title}</title> : null}
            {description ? <meta name="description" content={description} /> : null}
            <meta name="robots" content={robotsContent} />
            {canonical ? <link rel="canonical" href={canonical} /> : null}

            {ogTitle ? <meta property="og:title" content={ogTitle} /> : null}
            {ogDescription ? <meta property="og:description" content={ogDescription} /> : null}
            {ogUrl ? <meta property="og:url" content={ogUrl} /> : null}
            {og.type ? <meta property="og:type" content={og.type} /> : null}
            {og.locale ? <meta property="og:locale" content={og.locale} /> : null}
            {og.siteName ? <meta property="og:site_name" content={og.siteName} /> : null}

            {primaryImage?.url ? (
                <>
                    <meta property="og:image" content={primaryImage.url} />
                    {primaryImage.width ? (
                        <meta property="og:image:width" content={String(primaryImage.width)} />
                    ) : null}
                    {primaryImage.height ? (
                        <meta property="og:image:height" content={String(primaryImage.height)} />
                    ) : null}
                    {primaryImage.alt ? <meta property="og:image:alt" content={primaryImage.alt} /> : null}
                </>
            ) : null}

            {og.article?.publishedTime ? (
                <meta property="article:published_time" content={og.article.publishedTime} />
            ) : null}
            {og.article?.authors?.map((author) => (
                <meta key={author} property="article:author" content={author} />
            ))}
            {og.article?.tags?.map((tag) => (
                <meta key={tag} property="article:tag" content={tag} />
            ))}

            {tw.cardType ? <meta name="twitter:card" content={tw.cardType} /> : null}
            {tw.site ? <meta name="twitter:site" content={tw.site} /> : null}
            {tw.handle ? <meta name="twitter:creator" content={tw.handle} /> : null}
            {ogTitle ? <meta name="twitter:title" content={ogTitle} /> : null}
            {ogDescription ? <meta name="twitter:description" content={ogDescription} /> : null}
            {primaryImage?.url ? <meta name="twitter:image" content={primaryImage.url} /> : null}

            {metaTags.map((tag, index) =>
                tag.property ? (
                    <meta key={`meta-${index}`} property={tag.property} content={tag.content} />
                ) : (
                    <meta key={`meta-${index}`} name={tag.name} content={tag.content} />
                ),
            )}

            {additionalLinkTags.map((link, index) => (
                <link
                    key={`link-${index}`}
                    rel={link.rel}
                    href={link.href}
                    hrefLang={link.hrefLang}
                    type={link.type}
                />
            ))}
        </Head>
    );
}
